import { QueryHookOptions, useQuery } from '@apollo/client';
import { loader } from 'graphql.macro';
import moment from 'moment-timezone';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import { setSelectedCalendarMonth, setSelectedTimeSlot } from 'actions';
import { usePhoneId } from 'hooks/usePhoneId';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  DateTimeFormat,
  IState,
  ITimeSlotInfoForMonthResponse,
  ITimeSlotInfoForMonthVariables,
} from 'typings';

const timeSlotInfoForMonthQuery = loader(
  '../../queries/timeSlotInfoForMonth.graphql',
);

export const useTimeSlotInfoForMonthQuery = (
  options?: QueryHookOptions<
    ITimeSlotInfoForMonthResponse,
    ITimeSlotInfoForMonthVariables
  >,
) => {
  const {
    addressId,
    campaignMemberId,
    isEmergency,
    postalCode,
    selectedCalendarDate,
    selectedCalendarMonth,
    serviceCodeId,
    serviceType,
  } = useSelector((state: IState) => ({
    addressId: state.userInput.address.id,
    campaignMemberId: state.pmTracking.campaignMemberId,
    isEmergency: state.userInput.isEmergency,
    postalCode: state.userInput.address.zipCode,
    selectedCalendarDate: state.calendar.selectedCalendarDate,
    selectedCalendarMonth: state.calendar.selectedCalendarMonth,
    serviceCodeId: state.userInput.serviceCodeId,
    serviceType: state.userInput.serviceType,
  }));
  const dispatch = useThunkDispatch();
  const theme = useTheme();
  const phoneId = usePhoneId();

  const selectedMonth = selectedCalendarMonth
    ? moment(selectedCalendarMonth, DateTimeFormat.MONTH_COMPARISON)
    : null;

  const timeVariables = selectedMonth
    ? {
        month: selectedMonth.month() + 1,
        year: selectedMonth.year(),
      }
    : {
        month: null,
      };

  useEffect(() => {
    dispatch(setSelectedCalendarMonth(null));
  }, [
    addressId,
    campaignMemberId,
    theme.enterpriseEnabled,
    theme.enterpriseVersion,
    isEmergency,
    serviceCodeId,
    serviceType,
    dispatch,
  ]);

  useEffect(() => {
    dispatch(setSelectedTimeSlot(undefined));
  }, [
    addressId,
    campaignMemberId,
    theme.enterpriseEnabled,
    theme.enterpriseVersion,
    isEmergency,
    selectedCalendarDate,
    selectedCalendarMonth,
    serviceCodeId,
    serviceType,
    dispatch,
  ]);

  return useQuery<
    ITimeSlotInfoForMonthResponse,
    ITimeSlotInfoForMonthVariables
  >(timeSlotInfoForMonthQuery, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    pollInterval: 30000,
    skip: !serviceType || !serviceCodeId,
    variables: {
      addressId,
      campaignMemberId,
      enterpriseVersion: theme.enterpriseEnabled
        ? theme.enterpriseVersion
        : undefined,
      isEmergency,
      phoneId,
      serviceCodeId,
      serviceType,
      ...timeVariables,
      postalCode: addressId ? undefined : postalCode,
    },
    ...options,
  });
};
